import { useState, useRef, ReactNode, FC, useEffect, useCallback } from 'react';
import { useIsMounted } from 'Hooks/useIsMounted';
import { tailwindOverride } from '@getsynapse/design-system';
import { useScrollIntoViewWithPinned } from './hooks';

interface EditableTableRowCellProps {
  isEditable: boolean;
  editModeChildren: ReactNode;
  readModeChildren: ReactNode;
  className?: string;
  onSave?: () => void;
  onCancel?: () => void;
  onEnterEditMode?: () => void;
  showHoverInput?: boolean;
  hoverInputChildren?: ReactNode;
  closeTooltip?: () => void;
  openTooltip?: () => void;
  saveTriggerRef?: React.MutableRefObject<(() => void) | null>;
  cancelTriggerRef?: React.MutableRefObject<(() => void) | null>;
  editTriggerRef?: React.MutableRefObject<(() => void) | null>;

  /**
   * When true, the cell container does NOT enter edit mode on click. Edit mode
   * is then driven only by `editTriggerRef` (e.g. a click on a specific child
   * such as the name text or a pencil affordance) and by keyboard activation.
   * Lets callers restrict the click-to-edit target to part of the cell while
   * leaving the rest of the cell free to bubble (e.g. to a row navigation
   * handler). Defaults to false so existing callers are unaffected.
   */
  disableCellClick?: boolean;

  /**
   * Extra interactive area that should be treated as "inside" the cell,
   * e.g. a dropdown rendered via portal. Any click/focus inside this ref
   * will NOT trigger outside-click save/cancel.
   */
  externalInteractiveRef?: { current: HTMLElement | null };
}

export const EditableTableRowCell: FC<EditableTableRowCellProps> = ({
  isEditable,
  editModeChildren,
  readModeChildren,
  className = '',
  onSave,
  onCancel,
  onEnterEditMode,
  showHoverInput = false,
  hoverInputChildren,
  closeTooltip,
  openTooltip,
  saveTriggerRef,
  cancelTriggerRef,
  editTriggerRef,
  disableCellClick = false,
  externalInteractiveRef,
}) => {
  const [isEditModeOn, setIsEditModeOn] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);
  const isMounted = useIsMounted();

  const focusCell = useCallback(() => {
    requestAnimationFrame(() => {
      ref.current?.focus();
    });
  }, []);

  const handleClick = useCallback(() => {
    if (isEditable && !isEditModeOn) {
      onEnterEditMode?.();
      if (isMounted.current) setIsEditModeOn(true);
    }
  }, [isEditable, isEditModeOn, isMounted, onEnterEditMode]);

  const handleSave = useCallback(() => {
    if (!isMounted.current) return;
    setIsEditModeOn(false);
    onSave?.();
  }, [onSave, isMounted]);

  useEffect(() => {
    if (editTriggerRef) {
      editTriggerRef.current = handleClick;
    }
  }, [editTriggerRef, handleClick]);

  useEffect(() => {
    if (saveTriggerRef) {
      saveTriggerRef.current = handleSave;
    }
  }, [saveTriggerRef, handleSave]);

  const handleCancel = useCallback(
    (options?: { preserveFocus?: boolean }) => {
      if (!isMounted.current) return;
      setIsEditModeOn(false);
      setIsHovered(false);
      setIsFocused(options?.preserveFocus ?? false);
      if (options?.preserveFocus) {
        focusCell();
      }
      onCancel?.();
    },
    [onCancel, isMounted, focusCell]
  );

  useEffect(() => {
    if (cancelTriggerRef) {
      cancelTriggerRef.current = () => handleCancel();
    }
  }, [cancelTriggerRef, handleCancel]);

  const handleMouseEnter = () => {
    if (!isMounted.current) return;
    if (showHoverInput && !isEditModeOn) {
      setIsHovered(true);
    }
  };

  const handleMouseLeave = () => {
    if (!isMounted.current || !showHoverInput || isFocused) return;

    if (ref.current && !ref.current.matches(':hover')) {
      setIsHovered(false);
      if (!ref.current.contains(document.activeElement)) {
        setIsFocused(false);
      }
    }
  };

  const handleFocus = () => {
    if (!isMounted.current) return;
    if (showHoverInput && !isEditModeOn) {
      setIsFocused(true);
      setIsHovered(true);
    }

    if (!isEditable) {
      setTimeout(() => {
        openTooltip?.();
      }, 0);
    }
  };

  const handleBlur = () => {
    if (!isMounted.current || !showHoverInput) return;

    setIsFocused(false);
    if (ref.current && !ref.current.matches(':hover')) {
      setIsHovered(false);
    }

    if (!isEditable) {
      closeTooltip?.();
    }
  };

  const handleGlobalMouseMove = useCallback(
    (event: MouseEvent) => {
      if (!isMounted.current || !showHoverInput || isEditModeOn) return;

      if (ref.current) {
        const rect = ref.current.getBoundingClientRect();
        const x = event.clientX;
        const y = event.clientY;
        const isInside =
          x >= rect.left &&
          x <= rect.right &&
          y >= rect.top &&
          y <= rect.bottom;

        if (!isInside) {
          const hasFocus = ref.current.contains(document.activeElement);
          if (!hasFocus) {
            setIsHovered(false);
            setIsFocused(false);
            closeTooltip?.();
          }
        }
      }
    },
    [isMounted, showHoverInput, isEditModeOn, closeTooltip]
  );

  useEffect(() => {
    if (!isEditable) {
      if (isHovered && !isEditModeOn && showHoverInput) {
        document.addEventListener('mousemove', handleGlobalMouseMove);
        return () => {
          document.removeEventListener('mousemove', handleGlobalMouseMove);
        };
      }
      return;
    }

    const isTargetInExternal = (target: EventTarget | null) => {
      if (!target || !externalInteractiveRef?.current) return false;
      return externalInteractiveRef.current.contains(target as Node);
    };

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;

      const insideMain = ref.current && ref.current.contains(target);
      const insideExternal = isTargetInExternal(target);

      if (isMounted.current && isEditModeOn && !insideMain && !insideExternal) {
        handleSave();
        setIsHovered(false);
        setIsFocused(false);
      }
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (!isEditable || !isMounted.current) return;

      if (event.key === 'Enter' && !isEditModeOn && isFocused) {
        event.preventDefault();
        handleClick();
        return;
      }

      if (event.key === 'Enter' || event.key === 'Tab') {
        if (isEditModeOn) {
          event.preventDefault();
          handleSave();
          setIsHovered(false);
          setIsFocused(true);
          focusCell();
        }
      } else if (event.key === 'Escape') {
        event.preventDefault();
        handleCancel({ preserveFocus: true });
      }
    };

    const handleFocusChange = (event: FocusEvent) => {
      const target = event.target as Node;

      const insideMain = ref.current && ref.current.contains(target);
      const insideExternal = isTargetInExternal(target);

      if (
        isMounted.current &&
        (isEditModeOn || isHovered) &&
        !insideMain &&
        !insideExternal &&
        event.target instanceof HTMLInputElement
      ) {
        handleCancel();
      }
    };

    if (isEditModeOn || isHovered) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleKeyDown);
      document.addEventListener('focusin', handleFocusChange);

      if (isHovered && !isEditModeOn) {
        document.addEventListener('mousemove', handleGlobalMouseMove);
      }
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('focusin', handleFocusChange);
      document.removeEventListener('mousemove', handleGlobalMouseMove);
    };
  }, [
    isEditModeOn,
    isHovered,
    isFocused,
    handleSave,
    handleCancel,
    isEditable,
    isMounted,
    showHoverInput,
    handleClick,
    handleGlobalMouseMove,
    externalInteractiveRef,
    focusCell,
  ]);

  useScrollIntoViewWithPinned({
    isActive: isFocused || isEditModeOn,
    cellRef: ref,
    pinnedLeftWidth: 500,
    pinnedRightWidth: 200,
  });

  if (isEditModeOn && isEditable) {
    return (
      <div ref={ref} className={className}>
        {editModeChildren}
      </div>
    );
  }

  return (
    <div
      ref={ref}
      onClick={disableCellClick ? undefined : handleClick}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onFocus={handleFocus}
      onBlur={handleBlur}
      tabIndex={!isEditModeOn ? 0 : undefined}
      role={!isEditable ? 'textbox' : undefined}
      aria-readonly={!isEditable ? 'true' : undefined}
      className={tailwindOverride(
        'relative group w-full',
        'outline-none focus:outline-none focus-visible:outline-none',
        className
      )}
    >
      <div className='w-full'>
        {(isHovered || isFocused) && showHoverInput && !!hoverInputChildren
          ? hoverInputChildren
          : readModeChildren}
      </div>
    </div>
  );
};
